import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  PersonSearchResultExternalPersonScopeEnum as PersonScopeEnum,
  PersonSearchResultExternal,
} from '@askporter/client-grieg-lyric';
import { BaseAutocomplete, RevampedIcon, RevampedIconSize } from '@askporter/component-library';
import { ManagerRoles, useDebounce, fullName } from '@askporter/utils';
import doPeopleSearch from '../../PeopleSelector/utils/doPeopleSearch';

export interface MultiPeopleAutocompleteProps {
  idPrefix: string;
  label: string;
  disabled?: boolean;
  selectedPeople: PersonSearchResultExternal[];
  onChange: (people: PersonSearchResultExternal[]) => void;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  textFieldVariant?: 'outlined' | 'filled';
  t: (key: string, options?: Record<string, string | number>) => string;
  textOnlyWhenOutlined?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  readOnlyText?: React.ReactNode;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  maxValues?: number;
  inlineLabel?: boolean;
}

export const RevampedMultiPeopleAutocomplete = ({
  idPrefix,
  label,
  disabled,
  selectedPeople,
  onChange,
  onBlur = () => {},
  t,
  placeholder,
  readOnly = false,
  readOnlyText = undefined,
  fullWidth,
  size = 'medium',
  error,
  helperText,
  maxValues,
  inlineLabel,
}: MultiPeopleAutocompleteProps): JSX.Element => {
  const [people, setPeople] = useState<PersonSearchResultExternal[]>(selectedPeople);
  const [inputValue, setInputValue] = useState('');
  const peopleKeyword = useDebounce<string>(inputValue, 700);

  React.useEffect(() => {
    setPeople(selectedPeople);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPeople)]);

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, status } = useInfiniteQuery({
    queryKey: [`people/search`, { search: { freeText: peopleKeyword } }],
    queryFn: async ({ pageParam }) => {
      const response = await doPeopleSearch({
        search: {
          freeText: peopleKeyword,
          page: pageParam,
          filter: {
            scope: ['USERS', 'TEAMS'],
          },
        },
      });
      return { response, nextPage: pageParam + 1 };
    },
    initialPageParam: 1,
    enabled: peopleKeyword?.length > 2,
    getNextPageParam: (data) => {
      return data?.response?.summary?.totalPages >= data?.nextPage ? data?.nextPage : undefined;
    },
  });

  const getName = (person: PersonSearchResultExternal) => {
    if (person) {
      if (person?.personScope === PersonScopeEnum.User) {
        return fullName(person?.givenName, person?.familyName);
      }
      return person?.name;
    }
    return '';
  };

  const filterManagersTeams = (results: PersonSearchResultExternal[]) => {
    return results.filter(
      (entry) =>
        entry.personScope === PersonScopeEnum.Team ||
        (entry.personScope === PersonScopeEnum.User && ManagerRoles.includes(entry.roleSlug)),
    );
  };

  const opts =
    data?.pages && data?.pages.length > 0
      ? data.pages.reduce((acc, itm) => acc.concat(filterManagersTeams([...itm.response.results])), [])
      : [];

  // Combine search results and selected values, removing any duplicates (by uid)
  const additionalOptions: PersonSearchResultExternal[] = people || [];
  const selectedOptionsUids = additionalOptions.map((option) => option.uid);
  const filteredOptions = opts.filter((option) => !selectedOptionsUids.includes(option.uid));
  const options: PersonSearchResultExternal[] = [...additionalOptions, ...filteredOptions];

  return (
    <BaseAutocomplete
      multiple={true}
      t={t}
      label={label}
      idPrefix={idPrefix || 'multi-people-selector'}
      disabled={disabled}
      size={size}
      options={options}
      value={people}
      placeholder={placeholder}
      onChange={(newValue: PersonSearchResultExternal[]) => onChange(newValue)}
      onInputChange={(_: React.SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      inputValue={inputValue}
      loading={status === 'pending' || isFetchingNextPage}
      onScrollToBottom={() => hasNextPage && fetchNextPage()}
      startAdornment={
        <RevampedIcon fullPath="/media/icons/design-system/user/20px.svg" iconSize={RevampedIconSize.SM} />
      }
      mapOptionToValue={(option: PersonSearchResultExternal) => {
        return {
          uid: option.uid,
          label: getName(option),
          avatar: option?.profilePhoto?.filePath,
        };
      }}
      onBlur={(e) => onBlur(e)}
      fullWidth={fullWidth}
      error={error}
      helperText={helperText}
      maxValues={maxValues}
      inlineLabel={inlineLabel}
      {...{ readOnly, readOnlyText }}
    />
  );
};
